import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';

// Full-screen image inspector (rebuilt 2026-08-01, Chase's ask). Two distinct,
// SIMPLE modes so the touch experience isn't the old weird/inverted tap-zoom:
//
//  DESKTOP (fine pointer): the image sits big and static. A magnifier loupe
//    follows the cursor for close inspection, and a button turns it off. Left/
//    right arrows (and keyboard arrows) move between photos.
//
//  MOBILE (coarse pointer): NO tap-to-zoom. Swipe left/right to change photos,
//    pinch with two fingers to zoom in/out (drag to pan while zoomed), and
//    double-tap to toggle a quick zoom. Arrows are there too for taps.
//
// Backward compatible: accepts either `photos` (array of urls) + `startIndex`,
// or a single `url`. Closes on the X, a backdrop tap, or Escape.
const LOUPE = 200;        // loupe diameter (px)
const LOUPE_ZOOM = 2.5;   // desktop magnifier factor
const MAX_SCALE = 4;      // pinch ceiling
const DBL_TAP_SCALE = 2.5;

export default function Lightbox({ photos, url, startIndex = 0, alt, onClose, onIndexChange }) {
  const list = Array.isArray(photos) && photos.length ? photos : (url ? [url] : []);
  const [index, setIndex] = useState(Math.min(startIndex, Math.max(0, list.length - 1)));

  // One-time device mode: coarse pointer (touch-primary) → mobile gestures.
  const [isTouch] = useState(
    () => typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(pointer: coarse)').matches
      : false
  );

  // Desktop loupe
  const [loupeOn, setLoupeOn] = useState(true);
  const [loupe, setLoupe] = useState({ show: false, x: 0, y: 0, bgX: 0, bgY: 0, bgW: 0, bgH: 0 });

  // Mobile transform
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  const imgRef = useRef(null);
  const gesture = useRef(null);     // active touch gesture bookkeeping
  const lastTap = useRef(0);
  const poppedByBack = useRef(false);
  // Keep latest callbacks in refs so the mount-once effects below never re-run
  // (and never fire a stray history.back) just because a parent re-render made a
  // new onClose — that was navigating the page away on arrow clicks.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const goRef = useRef(null);

  const current = list[index];
  const many = list.length > 1;

  const resetZoom = useCallback(() => { setScale(1); setTx(0); setTy(0); }, []);

  const go = useCallback((dir) => {
    if (!many) return;
    setIndex((i) => {
      const next = (i + dir + list.length) % list.length;
      if (onIndexChange) onIndexChange(next);
      return next;
    });
    resetZoom();
    setLoupe((l) => ({ ...l, show: false }));
  }, [many, list.length, onIndexChange, resetZoom]);
  goRef.current = go;

  // Escape / arrow keys + body scroll lock. Mount-once (reads live callbacks
  // via refs) so navigating photos doesn't tear this down and rebuild it.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current();
      else if (e.key === 'ArrowRight') goRef.current(1);
      else if (e.key === 'ArrowLeft') goRef.current(-1);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, []);

  // Phone/browser BACK button should close the zoom, not navigate off the page
  // (matches eBay/native photo viewers). Push a throwaway history entry on open;
  // a back press pops it and closes here instead of leaving the product page.
  // Closing via the X/backdrop/Escape pops that same entry so we don't strand it.
  useEffect(() => {
    window.history.pushState({ svLightbox: true }, '');
    const onPop = () => { poppedByBack.current = true; onCloseRef.current(); };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (!poppedByBack.current) window.history.back();
    };
  }, []);

  // ── Desktop loupe ─────────────────────────────────────────────────────────
  const onMouseMove = (e) => {
    if (isTouch || !loupeOn) return;
    const el = imgRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    if (x < 0 || y < 0 || x > r.width || y > r.height) {
      setLoupe((l) => ({ ...l, show: false }));
      return;
    }
    const bgW = r.width * LOUPE_ZOOM;
    const bgH = r.height * LOUPE_ZOOM;
    setLoupe({
      show: true,
      x: e.clientX, y: e.clientY,
      bgX: -(x * LOUPE_ZOOM - LOUPE / 2),
      bgY: -(y * LOUPE_ZOOM - LOUPE / 2),
      bgW, bgH,
    });
  };
  const onMouseLeave = () => setLoupe((l) => ({ ...l, show: false }));

  // ── Mobile gestures ───────────────────────────────────────────────────────
  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

  const clampPan = (nx, ny, s) => {
    const el = imgRef.current;
    if (!el) return { x: nx, y: ny };
    const maxX = (el.offsetWidth * (s - 1)) / 2;
    const maxY = (el.offsetHeight * (s - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, nx)),
      y: Math.max(-maxY, Math.min(maxY, ny)),
    };
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      gesture.current = { kind: 'pinch', startDist: dist(e.touches), startScale: scale };
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      if (scale > 1) {
        gesture.current = { kind: 'pan', sx: t.clientX, sy: t.clientY, stx: tx, sty: ty };
      } else {
        gesture.current = { kind: 'swipe', sx: t.clientX, sy: t.clientY, dx: 0, dy: 0 };
      }
    }
  };

  const onTouchMove = (e) => {
    const g = gesture.current;
    if (!g) return;
    if (g.kind === 'pinch' && e.touches.length === 2) {
      e.preventDefault();
      const s = Math.max(1, Math.min(MAX_SCALE, g.startScale * (dist(e.touches) / g.startDist)));
      setScale(s);
      const c = clampPan(tx, ty, s);
      setTx(c.x); setTy(c.y);
    } else if (g.kind === 'pan' && e.touches.length === 1) {
      e.preventDefault();
      const t = e.touches[0];
      const c = clampPan(g.stx + (t.clientX - g.sx), g.sty + (t.clientY - g.sy), scale);
      setTx(c.x); setTy(c.y);
    } else if (g.kind === 'swipe' && e.touches.length === 1) {
      const t = e.touches[0];
      g.dx = t.clientX - g.sx;
      g.dy = t.clientY - g.sy;
    }
  };

  const onTouchEnd = (e) => {
    const g = gesture.current;
    gesture.current = null;
    if (!g) return;
    if (g.kind === 'swipe') {
      if (Math.abs(g.dx) > 50 && Math.abs(g.dx) > Math.abs(g.dy)) {
        go(g.dx < 0 ? 1 : -1);
        return;
      }
      // Not a swipe → treat as a tap. Double-tap toggles zoom; single tap on
      // the image does nothing (no more weird tap-zoom).
      const now = Date.now();
      if (now - lastTap.current < 300) {
        if (scale > 1) resetZoom();
        else setScale(DBL_TAP_SCALE);
        lastTap.current = 0;
      } else {
        lastTap.current = now;
      }
    } else if (g.kind === 'pinch') {
      if (scale < 1.05) resetZoom();
    }
  };

  if (!current) return null;

  const arrowBtn = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 3,
    width: 46, height: 46, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.28)', color: '#f4f2ff',
    cursor: 'pointer',
  };

  return createPortal(
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${alt} — image viewer`}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(6, 5, 14, 0.94)',
        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, overflow: 'hidden',
      }}
    >
      {/* Close */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        style={{
          position: 'absolute', top: 18, right: 20, zIndex: 4,
          padding: 10, borderRadius: 999, display: 'flex',
          background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)', color: '#f4f2ff',
          cursor: 'pointer',
        }}
      >
        <X size={20} />
      </button>

      {/* Desktop: magnifier toggle */}
      {!isTouch && (
        <button
          onClick={(e) => { e.stopPropagation(); setLoupeOn((v) => !v); setLoupe((l) => ({ ...l, show: false })); }}
          style={{
            position: 'absolute', top: 18, left: 20, zIndex: 4,
            padding: '8px 14px', borderRadius: 999, display: 'flex', alignItems: 'center', gap: 8,
            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)', color: '#f4f2ff',
            fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
          }}
        >
          {loupeOn ? <ZoomIn size={16} /> : <ZoomOut size={16} />}
          {loupeOn ? 'Magnifier on' : 'Magnifier off'}
        </button>
      )}

      {/* Prev / Next */}
      {many && (
        <>
          <button aria-label="Previous photo" onClick={(e) => { e.stopPropagation(); go(-1); }} style={{ ...arrowBtn, left: 14 }}>
            <ChevronLeft size={26} />
          </button>
          <button aria-label="Next photo" onClick={(e) => { e.stopPropagation(); go(1); }} style={{ ...arrowBtn, right: 14 }}>
            <ChevronRight size={26} />
          </button>
        </>
      )}

      {/* Image */}
      <img
        ref={imgRef}
        src={current}
        alt={alt}
        draggable={false}
        onClick={(e) => e.stopPropagation()}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        onTouchStart={isTouch ? onTouchStart : undefined}
        onTouchMove={isTouch ? onTouchMove : undefined}
        onTouchEnd={isTouch ? onTouchEnd : undefined}
        style={{
          maxWidth: '92vw', maxHeight: '86vh', objectFit: 'contain',
          transform: isTouch ? `translate(${tx}px, ${ty}px) scale(${scale})` : 'none',
          transition: (isTouch && !gesture.current) ? 'transform 0.18s ease' : 'none',
          transformOrigin: 'center center',
          cursor: (!isTouch && loupeOn) ? 'none' : 'default',
          userSelect: 'none', touchAction: 'none', borderRadius: 8,
        }}
      />

      {/* Desktop loupe */}
      {!isTouch && loupeOn && loupe.show && (
        <div
          style={{
            position: 'fixed', left: loupe.x - LOUPE / 2, top: loupe.y - LOUPE / 2,
            width: LOUPE, height: LOUPE, borderRadius: 999, zIndex: 5, pointerEvents: 'none',
            border: '2px solid rgba(255,255,255,0.85)', boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
            backgroundImage: `url(${current})`, backgroundRepeat: 'no-repeat',
            backgroundSize: `${loupe.bgW}px ${loupe.bgH}px`,
            backgroundPosition: `${loupe.bgX}px ${loupe.bgY}px`,
          }}
        />
      )}

      {/* Counter + hint */}
      <div
        style={{
          position: 'absolute', bottom: 22, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 16px', borderRadius: 999,
          background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
          color: '#cfc9e8', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.02em',
          pointerEvents: 'none', textAlign: 'center', maxWidth: '90vw',
        }}
      >
        {many && <span>{index + 1} / {list.length}</span>}
        <span style={{ opacity: 0.85 }}>
          {isTouch
            ? (many ? 'Swipe to change · pinch to zoom' : 'Pinch to zoom · double-tap')
            : (loupeOn ? 'Move to inspect' : 'Magnifier off') + (many ? ' · ← → to browse' : '')}
        </span>
      </div>
    </div>,
    document.body,
  );
}
